import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

/**
 * Inline Alert (audit D3). cva status variants keyed to the A1 semantic status
 * tokens shipped by plan 01 — each status renders soft-bg + hairline soft-border
 * + soft-text from those tokens, never an invented color. `rounded-card` (the
 * card-tier asymmetric corner pair), no box-shadow. Text-only content (React
 * children); consuming apps supply i18n strings via the label props (D-20).
 */
const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-card border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground border-border",
        success:
          "bg-success-soft border-success-soft-border text-success-soft-foreground",
        warning:
          "bg-warning-soft border-warning-soft-border text-warning-soft-foreground",
        info: "bg-info-soft border-info-soft-border text-info-soft-foreground",
        destructive: "bg-destructive/10 border-destructive/25 text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface AlertProps
  extends Omit<React.ComponentProps<"div">, "title">,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Optional leading icon (rendered as the first grid cell). */
  icon?: React.ReactNode;
}

function Alert({
  className,
  variant = "default",
  title,
  description,
  icon,
  role,
  children,
  ...props
}: AlertProps) {
  // warning/destructive are assertive live regions; the rest are polite.
  const resolvedRole =
    role ?? (variant === "warning" || variant === "destructive" ? "alert" : "status");

  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role={resolvedRole}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon}
      <div className="col-start-2 grid gap-1">
        {title ? (
          <div data-slot="alert-title" className="font-semibold leading-none">
            {title}
          </div>
        ) : null}
        {description ? (
          <div data-slot="alert-description" className="text-sm opacity-90">
            {description}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export { Alert, alertVariants };
